import {createElem} from 'common/util/dom';
import * as colors from 'common/util/colors';
import {parseStateFromUrl} from 'common/report/util/state-from-url';
import {isTestEnv} from 'common/report/util/env';

import {injectCssIntoIframeWhenAttached} from './util';
import css from './apercu.inline.styl';

export enum ZendeskDisplayMode {
  Chat = `chat`,
  Help = `helpCenter`,
  Hidden = `hidden`,
}

export enum ChatDepartment {
  Startup = `Startup`, // deprecated
  Support = `Support`,
}

enum ZendeskWidgetTrackingEvent {
  LiveChatStarted = `Live Chat Started`,
}

export type ZendeskWidget = any;

const ZD_STORAGE_KEY = `ZD-store`;

const ZENDESK_SCRIPT_SRC = `https://assets.zendesk.com/embeddable_framework/main.js`;

/**
 * Zendesk provides two ways to explicitly set suggestions:
 * 1. filter by specific "label" which is set on the help document
 * 2. filter by a search term which will mimic the way a user searches
 * {url: true} makes the widget use the url to automatically come up with suggestions
 */
export type ZendeskHelpSuggestions = {labels: string[]} | {search: string} | {url: boolean};

function gracefulJsonParse(json) {
  if (json) {
    return JSON.parse(json);
  }
  return null;
}

export function hideZendeskWidget() {
  window.zE?.hide();
}

export function activatePersistedZendeskChat(
  chatDepartment: ChatDepartment,
  userName: string,
  userEmail: string,
): boolean {
  const zdStore = gracefulJsonParse(window.localStorage.getItem(ZD_STORAGE_KEY));
  if (zdStore && zdStore.widgetShown && zdStore.activeEmbed === ZendeskDisplayMode.Chat) {
    activateZendeskChat(chatDepartment, userName, userEmail);
    return true;
  }
  return false;
}

export async function activateZendeskHelp(suggestions?: ZendeskHelpSuggestions) {
  const zdWidget = await loadZendeskWidget();
  if (!zdWidget) {
    return;
  }

  if (suggestions) {
    setHelpCenterSuggestions(suggestions);
  } else {
    updateHelpCenterFromUrl();
  }

  zdWidget(`webWidget`, `updateSettings`, {
    webWidget: {
      chat: {
        suppress: true,
      },
      contactForm: {
        suppress: true,
      },
      helpCenter: {
        suppress: false,
      },
    },
  });

  zdWidget.activate();
}

// Only update if embedded
export function setHelpCenterSuggestions(suggestions: ZendeskHelpSuggestions) {
  window.zE?.(`webWidget`, `helpCenter:setSuggestions`, suggestions);
}

export function updateHelpCenterFromUrl() {
  const {appName} = parseStateFromUrl(window.location.pathname) || {};
  if (appName) {
    setHelpCenterSuggestions({search: appName});
  } else {
    setHelpCenterSuggestions({url: true});
  }
}

export async function activateZendeskChat(chatDepartment: ChatDepartment, userName: string, userEmail: string) {
  const zdWidget = await loadZendeskWidget();
  if (!zdWidget) {
    return;
  }

  zdWidget(`webWidget`, `updateSettings`, {
    webWidget: {
      chat: {
        departments: {
          enabled: [chatDepartment],
          select: chatDepartment,
        },
        suppress: false,
      },
      helpCenter: {
        suppress: true,
      },
    },
  });

  zdWidget(`webWidget:on`, `chat:start`, () => {
    mixpanel.people.increment(`[Help Widget] ${ZendeskWidgetTrackingEvent.LiveChatStarted}`, 1);
    mixpanel.track(`[Help Widget] ${ZendeskWidgetTrackingEvent.LiveChatStarted}`, {
      $current_url: window.location.href,
      Department: chatDepartment,
    });
  });

  zdWidget.activate();
  zdWidget.identify({
    name: userName,
    email: userEmail,
  });
}

function embedZendeskScript() {
  // see: https://developer.zendesk.com/embeddables/docs/widget/gtm
  const w: any = window;
  const d: any = document;
  d.zendeskHost = `mixpanelsupport.zendesk.com`;

  if (!w.zEmbed) {
    const queue = [];
    w.zEmbed = function () {
      queue.push(arguments); // eslint-disable-line prefer-rest-params
    };
    w.zE = w.zE || w.zEmbed;
    d.zEQueue = queue;

    w.zESettings = {
      webWidget: {
        color: {theme: colors.blue1},
      },
    };
  }

  if (!document.head.querySelector(`script[src='${ZENDESK_SCRIPT_SRC}']`)) {
    document.head.appendChild(
      createElem(`script`, {
        src: ZENDESK_SCRIPT_SRC,
        'data-ze-csp': `true`,
        async: true,
        defer: true,
      }),
    );
  }
}

let _zdWidgetPromise: Promise<ZendeskWidget>;
async function loadZendeskWidget() {
  if (isTestEnv()) {
    return null;
  }

  /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
  if (!_zdWidgetPromise) {
    _zdWidgetPromise = new Promise((resolve, reject) => {
      embedZendeskScript();
      if (!window.zE) {
        reject(`Failed to embed Zendesk script`);
      }

      // Propagate any widget userEvents events to mixpanel
      window.zE(`webWidget:on`, `userEvent`, (eventObject) => {
        mixpanel.track(`[Zendesk Widget] ${eventObject.action}`, eventObject.properties);
      });

      // Zendesk "ready" callback to resolve that the script has been loaded.
      window.zE(() => {
        injectCssIntoIframeWhenAttached(`iframe#webWidget`, css);

        resolve(window.zE);
      });
    });
  }

  return _zdWidgetPromise;
}
